import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatCurrency, moneyAmountLabel } from '@/utils/formatCurrency';
import { adsApi } from '@/api/client';
import { AdGroupFields } from '@/components/campaign-create/AdGroupFields';
import { FormActions } from '@/components/campaign-create/CreateSpCampaignForm';
import {
  campaignBudgetMin,
  defaultAdGroupName,
  defaultCampaignBudgetInput,
  defaultCampaignDateRange,
  extractAdGroupMutationResults,
  extractSdCampaignMutateMessages,
  formatCreateCampaignUpstreamError,
  isoDateToSd,
  validateCampaignBudget,
} from '@/utils/campaignCreate';

type SdTactic = 'T00020' | 'T00030';

export function CreateSdCampaignForm({
  profileId,
  onSuccess,
  onCancel,
}: {
  profileId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [campaignState, setCampaignState] = useState<'enabled' | 'paused'>('enabled');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [budget, setBudget] = useState(() => defaultCampaignBudgetInput('SD'));
  const [costType, setCostType] = useState<'cpc'>('cpc');
  const [tactic, setTactic] = useState<SdTactic>('T00030');
  const [adGroupName, setAdGroupName] = useState('');
  const [adGroupNameTouched, setAdGroupNameTouched] = useState(false);
  const [defaultBid, setDefaultBid] = useState('1');
  const [bidOptimization, setBidOptimization] = useState('clicks');
  const [creativeType, setCreativeType] = useState('IMAGE');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const budgetMin = campaignBudgetMin('SD');
  const qc = useQueryClient();

  useEffect(() => {
    const { startDate: s, endDate: e } = defaultCampaignDateRange();
    setName('');
    setCampaignState('enabled');
    setStartDate(s);
    setEndDate(e);
    setBudget(defaultCampaignBudgetInput('SD'));
    setCostType('cpc');
    setTactic('T00030');
    setAdGroupName('');
    setAdGroupNameTouched(false);
    setDefaultBid('1');
    setBidOptimization('clicks');
    setCreativeType('IMAGE');
    setSubmitError(null);
  }, []);

  useEffect(() => {
    if (!adGroupNameTouched) {
      setAdGroupName(defaultAdGroupName(name));
    }
  }, [name, adGroupNameTouched]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setSubmitError('Campaign name is required.');
      return;
    }
    const budgetNum = parseFloat(budget);
    const budgetError = validateCampaignBudget(budgetNum, 'SD');
    if (budgetError) {
      setSubmitError(budgetError);
      return;
    }
    if (!startDate || !endDate) {
      setSubmitError('Start and end dates are required.');
      return;
    }
    if (endDate < startDate) {
      setSubmitError('End date must be on or after start date.');
      return;
    }
    const trimmedAdGroupName = adGroupName.trim();
    if (!trimmedAdGroupName) {
      setSubmitError('Ad group name is required.');
      return;
    }
    const defaultBidNum = parseFloat(defaultBid);
    if (Number.isNaN(defaultBidNum) || defaultBidNum <= 0) {
      setSubmitError('Default bid must be greater than 0.');
      return;
    }

    const campaignPayload: Record<string, unknown> = {
      name: trimmed,
      budgetType: 'daily',
      budget: budgetNum.toFixed(2),
      startDate: isoDateToSd(startDate),
      endDate: isoDateToSd(endDate),
      costType,
      state: campaignState,
      tactic,
    };

    setSubmitting(true);
    try {
      const res = await adsApi.createSdCampaigns(profileId, { campaigns: [campaignPayload] });
      if (!res.success) {
        setSubmitError(res.message || 'Failed to create campaign');
        return;
      }
      const { errors, successIds } = extractSdCampaignMutateMessages(res.data);
      if (errors.length > 0) {
        setSubmitError(errors.join(' '));
        return;
      }
      const campaignId = successIds[0];
      if (!campaignId) {
        setSubmitError('Campaign was created but no campaign ID was returned.');
        return;
      }

      const agRes = await adsApi.createSdAdGroups(profileId, {
        adGroups: [
          {
            name: trimmedAdGroupName,
            campaignId: Number(campaignId),
            defaultBid: defaultBidNum,
            bidOptimization,
            state: 'enabled',
            creativeType,
          },
        ],
      });
      if (!agRes.success) {
        setSubmitError(
          `Campaign created, but ad group failed: ${agRes.message || 'Unknown error'}`
        );
        await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
        return;
      }
      const agParsed = extractAdGroupMutationResults(agRes.data);
      if (agParsed.errors.length > 0) {
        setSubmitError(`Campaign created, but ad group failed: ${agParsed.errors.join(' ')}`);
        await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
        return;
      }
      if (agParsed.successIds.length === 0) {
        setSubmitError('Campaign created, but ad group ID was not returned.');
        await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
        return;
      }

      await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
      await qc.invalidateQueries({ queryKey: ['sd-ad-groups', profileId] });
      onSuccess();
    } catch (err) {
      setSubmitError(formatCreateCampaignUpstreamError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-sm text-foreground-muted mb-4">
        Creates a Sponsored Display campaign and ad group via Amazon&apos;s SD APIs.
      </p>
      {submitError && (
        <div className="mb-4 text-sm text-error bg-error-subtle border border-error/20 rounded-xl px-3 py-2">
          {submitError}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">Campaign name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-border rounded-lg px-3 py-2"
            disabled={submitting}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">State</label>
            <select
              value={campaignState}
              onChange={(e) => setCampaignState(e.target.value as 'enabled' | 'paused')}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="enabled">Enabled</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Cost type</label>
            <select
              value={costType}
              onChange={(e) => setCostType(e.target.value as 'cpc')}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="cpc">CPC</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">Tactic</label>
          <select
            value={tactic}
            onChange={(e) => setTactic(e.target.value as SdTactic)}
            className="w-full border border-border rounded-lg px-3 py-2 text-sm"
            disabled={submitting}
          >
            <option value="T00020">Context targeting (retargeting)</option>
            <option value="T00030">Audience targeting</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">{moneyAmountLabel('Daily budget')}</label>
          <input
            type="number"
            step="0.01"
            min={budgetMin ?? undefined}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className="w-full border border-border rounded-lg px-3 py-2"
            disabled={submitting}
          />
          <p className="text-xs text-foreground-subtle mt-1">
            Sent to Amazon as a decimal string.
            {budgetMin != null ? ` Minimum ${formatCurrency(budgetMin)}.` : ''}
          </p>
        </div>

        <AdGroupFields
          adGroupName={adGroupName}
          onAdGroupNameChange={(value) => {
            setAdGroupNameTouched(true);
            setAdGroupName(value);
          }}
          defaultBid={defaultBid}
          onDefaultBidChange={setDefaultBid}
          showDefaultBid
          showSdFields
          bidOptimization={bidOptimization}
          onBidOptimizationChange={setBidOptimization}
          creativeType={creativeType}
          onCreativeTypeChange={setCreativeType}
          disabled={submitting}
        />

        <FormActions submitting={submitting} onCancel={onCancel} />
      </form>
    </>
  );
}
